import {
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AccountCircle,
  Assessment,
  Assignment,
  Business,
  Dashboard,
  DirectionsCar,
  ExpandLess,
  ExpandMore,
  InsertDriveFile,
  LocalShipping,
  LocationOn,
  Notifications,
  People,
  Straighten,
  Timeline,
} from "@material-ui/icons";
import CommentIcon from "@material-ui/icons/Comment";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import History from "@material-ui/icons/Map";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Warning from "@material-ui/icons/Warning";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../../config/config";

const drawerWidthExpanded = 290;
const drawerWidthCollapsed = 70;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: (props) =>
      props.isExpanded ? drawerWidthExpanded : drawerWidthCollapsed,
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),

    zIndex: 1000,
  },
  drawerPaper: {
    width: (props) =>
      props.isExpanded ? drawerWidthExpanded : drawerWidthCollapsed,
    background: "linear-gradient(135deg, #024a85ff 0%, #003a6b 100%)",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    transition: theme.transitions.create(["width", "box-shadow"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginTop: "70px",
    overflowX: "hidden",
    paddingBottom: "100px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 1),
    background: "rgba(255,255,255,0.1)",
    minHeight: 70,
  },
  logo: {
    color: "white",
    fontWeight: "bold",
    fontSize: "1.2rem",
  },
  toggleButton: {
    color: "white",
    background: "rgba(255,255,255,0.1)",
  },
  menuItem: {
    margin: theme.spacing(0.5, 1),
    borderRadius: 12,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.15)",
      transform: "translateX(5px)",
    },
    "&.active": {
      background: "rgba(255,255,255,0.2)",
      boxShadow: "0 4px 15px rgba(255,255,255,0.1)",
    },
  },
  menuIcon: {
    color: "white",
    minWidth: 40,
    opacity: 0.8,
  },
  menuText: {
    color: "white",
    fontWeight: 500,
    "& .MuiListItemText-primary": {
      fontSize: "0.9rem",
    },
  },
  submenu: {
    paddingLeft: theme.spacing(4),
    background: "rgba(0,0,0,0.1)",
    borderRadius: 8,
  },
  submenuItem: {
    borderRadius: 8,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.1)",
    },
    "&.active": {
      background: "rgba(255,255,255,0.2)",
      boxShadow: "0 4px 15px rgba(255,255,255,0.1)",
    },
  },
  submenuIcon: {
    color: "rgba(255,255,255,0.7)",
    minWidth: 35,
  },
  submenuText: {
    color: "rgba(255,255,255,0.9)",
    "& .MuiListItemText-primary": {
      fontSize: "0.8rem",
    },
  },
  expandIcon: {
    color: "white",
    opacity: 0.7,
  },
  divider: {
    background: "rgba(255,255,255,0.1)",
    margin: theme.spacing(1, 2),
  },
}));

// Submenu Component
const Submenu = React.memo(
  ({ children, isOpen, classes, handleItemClick, activeItem }) => (
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <List className={classes.submenu} disablePadding>
        {children.map((child) => (
          <ListItem
            button
            key={child.name}
            className={`${classes.submenuItem} ${
              activeItem === child.name ? "active" : ""
            }`}
            onClick={(e) => handleItemClick(e, child.name, child.path)}
          >
            <ListItemIcon className={classes.submenuIcon}>
              {child.icon}
            </ListItemIcon>
            <ListItemText
              primary={child.label}
              className={classes.submenuText}
            />
          </ListItem>
        ))}
      </List>
    </Collapse>
  ),
);

// Menu Item Component
const MenuItem = React.memo(
  ({
    item,
    isExpanded,
    isActive,
    isOpen,
    classes,
    onClick,
    handleItemClick,
    activeItem,
  }) =>
    item ? (
      <>
        <ListItem
          button
          className={`${classes.menuItem} ${isActive ? "active" : ""}`}
          onClick={onClick}
        >
          <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
          {isExpanded && (
            <>
              <ListItemText primary={item.label} className={classes.menuText} />
              {item.hasSubmenu && (
                <div className={classes.expandIcon}>
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </div>
              )}
            </>
          )}
        </ListItem>

        {item.hasSubmenu && item.children?.length > 0 && (
          <Submenu
            children={item.children}
            isOpen={isOpen && isExpanded}
            classes={classes}
            handleItemClick={handleItemClick}
            activeItem={activeItem}
          />
        )}
      </>
    ) : null,
);

const Sidebar = React.memo(({ isExpanded, setIsExpanded }) => {
  // const [isExpanded, setIsExpanded] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [activeItem, setActiveItem] = useState("CURRENT_OPERATIONAL_STATUS");
  const navigate = useNavigate();
  const location = useLocation(); // NEW
  const classes = useStyles({ isExpanded });

  const menuVisibility = useMemo(() => {
    const permissionAccess = JSON.parse(sessionStorage.getItem("data")) || {};
    return permissionAccess?.menuVisiblity || [];
  }, []);

  // Check menu visibility
  const isMenuVisible = useCallback(
    (menuName) => {
      const menuItem = menuVisibility.find((item) => item.name === menuName);
      return menuItem ? menuItem.isVisible : false;
    },
    [menuVisibility],
  );

  // Check submenu visibility
  const getVisibleSubmenus = useCallback(
    (menuName) => {
      const menuItem = menuVisibility.find((item) => item.name === menuName);
      if (!menuItem || !menuItem.subMenuMasterDTOList) return [];

      return menuItem.subMenuMasterDTOList.filter(
        (submenu) =>
          // You can add additional logic here if needed
          true, // For now, showing all submenus that exist in the list
      );
    },
    [menuVisibility],
  );

  const handleSubmenuToggle = useCallback((menuName) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  }, []);

  // const handleItemClick = useCallback(
  //   (itemName, path) => {
  //     setActiveItem(itemName);
  //     navigate(path);
  //   },
  //   [navigate]
  // );
  const handleItemClick = useCallback(
    (event, itemName, path) => {
      setActiveItem(itemName);

      if (event.ctrlKey || event.metaKey) {
        // Ctrl (Windows/Linux) or Command (Mac)
        window.open(path, "_blank");
      } else {
        navigate(path);
      }
    },
    [navigate],
  );

  // Main menu items with visibility mapping
  const menuItems = useMemo(() => {
    const items = [
      {
        name: "DASHBOARD",
        label: "Dashboard",
        icon: <Dashboard />,
        path: "/dashboard",
        visible: isMenuVisible("DASHBOARD"),
      },
      {
        name: "ADMINISTRATION_MANAGEMENT",
        label: "Administration Management",
        icon: <People />,
        hasSubmenu: true,
        visible: isMenuVisible("ADMINISTRATION_MANAGEMENT"),
        children: [
          {
            name: "User Management",
            label: "User Management",
            icon: <People />,
            path: "/userlist",
          },
          {
            name: "Role & Permission",
            label: "Role & Permission",
            icon: <AccountCircle />,
            path: "/rolePermissionManager",
          },
          // {
          //   name: "Role & Permission",
          //   label: "Vehicle Configuration",
          //   icon: <DirectionsCar />,
          //   path: "/vehicleConfigurationManager",
          // },
        ].filter((child) => {
          // Filter children based on submenu visibility
          const visibleSubmenus = getVisibleSubmenus(
            "ADMINISTRATION_MANAGEMENT",
          );
          return visibleSubmenus.some((sub) => sub.name === child.name);
        }),
      },
      {
        name: "TRANSPORTER_MANAGEMENT",
        label: "Transporter Management",
        icon: <LocalShipping />,
        path: "/transporterlist",
        visible: isMenuVisible("TRANSPORTER_MANAGEMENT"),
      },
      {
        name: "SITE_MANAGEMENT",
        label: "Operating Stations",
        icon: <LocationOn />,
        path: "/sitelist",
        visible: isMenuVisible("SITE_MANAGEMENT"),
      },
      {
        name: "SITE DISTANCE MANAGEMENT",
        label: "Site Distance Configuration",
        icon: <Straighten />,
        path: "/siteDistancelist",
        visible: isMenuVisible("SITE DISTANCE MANAGEMENT"),
      },
      {
        name: "VEHICLE_MANAGEMENT",
        label: "Vehicle Management",
        icon: <DirectionsCar />,
        path: "/vehiclelist",
        visible: isMenuVisible("VEHICLE_MANAGEMENT"),
      },
      {
        name: "RAW_DATA_MASTER",
        label: "Raw Data Master",
        icon: <InsertDriveFileIcon />,
        path: "/RewDataList",
        visible: isMenuVisible("RAW_DATA_MASTER"),
      },
      {
        name: "CONFIGURATION_MASTER",
        label: "Configuration Master",
        icon: <Assignment />,
        path: "/ConfigurationMaster",
        visible: isMenuVisible("CONFIGURATION_MASTER"),
      },
      {
        name: "CURRENT_OPERATIONAL_STATUS",
        label: "Current Operational Status",
        icon: <Business />,
        path: "/currentOperationalStatus",
        visible: isMenuVisible("CURRENT_OPERATIONAL_STATUS"),
      },
      {
        name: "VEHICLE_INTERRACTION",
        label: "Vehicle Interaction Notes",
        icon: <CommentIcon />,
        path: "/vehicleInteractionNotes",
        visible: isMenuVisible("VEHICLE_INTERRACTION"),
      },
      {
        name: "DATA_LOSS_REPORT",
        label: "Data Loss Report",
        icon: <Warning />,
        path: "/DataLoss",
        visible: isMenuVisible("DATA_LOSS_REPORT"),
      },
      // {
      //   name: "ROUTE_MANAGEMENT",
      //   label: "Route Management",
      //   icon: <Settings />,
      //   path: "/routelist",
      //   visible: isMenuVisible("ROUTE_MANAGEMENT"),
      // },
      {
        name: "TRACK_HISTORY",
        label: "Map View",
        icon: <History />,
        path: "/history/0",
        visible: isMenuVisible("TRACK_HISTORY"),
      },
      {
        name: "TRIP_MANAGEMENT",
        label: "Trip Management",
        icon: <Assignment />,
        path: "/triplist",
        visible: isMenuVisible("TRIP_MANAGEMENT"),
      },
      {
        name: "NOTIFICATION_CONFIG",
        label: "Notification Configuration",
        icon: <Notifications />,
        path: "/notificationConfigure",
        visible: isMenuVisible("NOTIFICATION CONFIGURATION"),
      },
      {
        name: "NIT_MASTER",
        label: "Nit Master",
        icon: <InsertDriveFile />,
        path: "/NitMaster",
        visible: isMenuVisible("NIT_MASTER"),
      },
      {
        name: "TRIP_PERFORMANCE",
        label: "Trip Performance",
        icon: <Timeline />,
        path: "/TripPerformance",
        visible: isMenuVisible("TRIP_PERFORMANCE"),
      },
      {
        name: "LANDMARK_MANAGEMENT",
        label: "Landmark Management",
        icon: <LocationOn />,
        path: "/LandmarkList",
        visible: isMenuVisible("LANDMARK_MANAGEMENT"),
      },
    ];

    // Filter visible items and ensure submenus have visible children
    return items.filter((item) => {
      if (!item.visible) return false;
      if (item.hasSubmenu) {
        return item.children && item.children.length > 0;
      }
      return true;
    });
  }, [isMenuVisible, getVisibleSubmenus]);

  // Report items with visibility mapping
  const reportItems = useMemo(() => {
    const items = [
      {
        name: "REPORTS",
        label: "Reports",
        icon: <Assessment />,
        hasSubmenu: true,
        visible: isMenuVisible("REPORTS"),
        children: [
          {
            name: "Gps Report",
            label: "Gps Report",
            icon: <LocationOn />,
            path: "/reportsgps",
          },
          {
            name: "Daily Uptime",
            label: "UpTime Report",
            icon: <LocationOn />,
            path: "/upTime",
          },
          {
            name: "Monthly Uptime",
            label: "UpTime Monthly Report",
            icon: <LocationOn />,
            path: "/uptimeMonthly",
          },
          {
            name: "LEAD WISE DISTANCE REPORT",
            label: "Lead Wise Distance Report",
            icon: <LocationOn />,
            path: "/nitReport",
          },
        ].filter((child) => {
          const visibleSubmenus = getVisibleSubmenus("REPORTS");
          return visibleSubmenus.some((sub) => sub.name === child.name);
        }),
      },
      {
        name: "VEHICLE_REPORT",
        label: "Vehicle Reports",
        icon: <DirectionsCar />,
        hasSubmenu: true,
        visible: isMenuVisible("VEHICLE_REPORT"),
        children: [
          // {
          //   name: "MOVEMENT_REPORT",
          //   label: "Movement Report",
          //   icon: <Speed />,
          //   path: "/reports",
          // },

          {
            name: "VEHICLE STATUS",
            label: "Vehicle Status",
            icon: <Assessment />,
            path: "/vehicleStatus",
          },

          {
            name: "Notification Report",
            label: "Alert Report",
            icon: <Assessment />,
            path: "/notification",
          },
          {
            name: "ALERT APPROVAL",
            label: "Approval Alerts",
            icon: <Assessment />,
            path: "/approvalAlert",
          },
          {
            name: "VEHICLE STUCK REPORT",
            label: "Vehicle Stuck Report",
            icon: <Assessment />,
            path: "/vehicleStuckAlert",
          },
        // ].filter((child) => {
        //   const visibleSubmenus = getVisibleSubmenus("VEHICLE_REPORT");
        //   if (child.name === "VEHICLE STATUS") {
        //     return true;
        //   }
        //   return visibleSubmenus.some((sub) => sub.name === child.name);
        // }),


].filter((child) => {
  const visibleSubmenus = getVisibleSubmenus("VEHICLE_REPORT");

  return visibleSubmenus.some((sub) => sub.name === child.name);
}),

      },
    ];

    return items.filter((item) => {
      if (!item.visible) return false;
      if (item.hasSubmenu) {
        return item.children && item.children.length > 0;
      }
      return true;
    });
  }, [isMenuVisible, getVisibleSubmenus]);

  const pathToName = useMemo(() => {
    const map = {};
    [...menuItems, ...reportItems].forEach((item) => {
      if (item.path) map[item.path] = item.name;
      item.children?.forEach((child) => {
        if (child.path) map[child.path] = child.name;
      });
    });
    return map;
  }, [menuItems, reportItems]);

  useEffect(() => {
    console.log("yashhere", location.pathname);
    const matchedName = pathToName[location.pathname];
    if (!matchedName) return;

    setActiveItem((prev) => (prev === matchedName ? prev : matchedName));

    const parent = [...menuItems, ...reportItems].find((item) =>
      item.children?.some((child) => child.name === matchedName),
    );
    if (parent) {
      setOpenSubmenus((prev) =>
        prev[parent.name] ? prev : { ...prev, [parent.name]: true },
      );
    }
  }, [location.pathname, pathToName, menuItems, reportItems]);

  return (
    <Drawer
      variant="permanent"
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <List>
        {/* Main Menu Items */}
        {menuItems.map((item) => {
          const isParentActive =
            activeItem === item.name ||
            (item.hasSubmenu &&
              item.children?.some((child) => child.name === activeItem));

          return (
            <MenuItem
              key={item.name}
              item={item}
              isExpanded={isExpanded}
              isActive={isParentActive}
              isOpen={openSubmenus[item.name]}
              classes={classes}
              onClick={(e) =>
                item.hasSubmenu
                  ? handleSubmenuToggle(item.name)
                  : handleItemClick(e, item.name, item.path)
              }
              handleItemClick={handleItemClick}
              activeItem={activeItem}
            />
          );
        })}

        {/* Show divider only if there are report items to show */}
        {reportItems.length > 0 && <Divider className={classes.divider} />}

        {/* Report Menu Items */}
        {reportItems.map((item) => {
          const isParentActive =
            activeItem === item.name ||
            (item.hasSubmenu &&
              item.children?.some((child) => child.name === activeItem));

          return (
            <MenuItem
              key={item.name}
              item={item}
              isExpanded={isExpanded}
              isActive={isParentActive}
              isOpen={openSubmenus[item.name]}
              classes={classes}
              onClick={(e) =>
                item.hasSubmenu
                  ? handleSubmenuToggle(item.name)
                  : handleItemClick(e, item.name, item.path)
              }
              handleItemClick={handleItemClick}
              activeItem={activeItem}
            />
          );
        })}
      </List>
      <div style={{ position: "fixed", bottom: "0" }}>
        <div className=" ">
          <div></div>
          <div
            className="text-white font-weight-bolder text-center ml-1"
            style={{ fontSize: "10px" }}
          >
            Version {config.version}{" "}
          </div>
        </div>
      </div>
    </Drawer>
  );
});

export default Sidebar;
